<script lang="ts" setup>
    import { storeToRefs } from 'pinia'
    import { useAppStore } from '~~/stores/app'
    import { useContextStore } from '~~/stores/context'
    import { useAwsStore } from '~~/stores/aws'
    import { useBetterStackStore } from '~~/stores/better-stack'
    import { useBurnerMailStore } from '~~/stores/burner-mail'
    import { useForwardEmailStore } from '~~/stores/forward-email'
    import { useGoogleStore } from '~~/stores/google'
    import { useGridPaneStore } from '~~/stores/gridpane'
    import { useNotionStore } from '~~/stores/notion'
    import { usePloiStore } from '~~/stores/ploi'
    import { useTallyfyStore } from '~~/stores/tallyfy'
    import { useEvents } from '~~/app/composables/useEvents'
    import { useDownloadsStore } from '~~/stores/downloads'

    const appStore = useAppStore()
    const downloadsStore = useDownloadsStore()

    // Establish global SSE connection for real-time events
    useEvents().connect()

    // Restore persisted user contexts from DB. Provider labels resolve lazily
    // per provider via the context store (see BzContextBar.ensureProvider).
    useContextStore().init()

    downloadsStore.init()

    // Instantiate all provider stores so they register their provider names immediately.
    useAwsStore()
    useBetterStackStore()
    useBurnerMailStore()
    useForwardEmailStore()
    useGoogleStore()
    useGridPaneStore()
    useNotionStore()
    usePloiStore()
    useTallyfyStore()

    const {
        isMenuOpen,
        isLogOpen,
        menuWidth,
        logWidth,
    } = storeToRefs(appStore)

    function startResize(side: 'left' | 'right') {
        const onMove = (e: MouseEvent) => {
            if (side === 'left') {
                menuWidth.value = Math.max(150, e.clientX)
            }
            else {
                logWidth.value = Math.max(150, window.innerWidth - e.clientX)
            }
        }
        const onUp = () => {
            document.removeEventListener('mousemove', onMove)
            document.removeEventListener('mouseup', onUp)
        }
        document.addEventListener('mousemove', onMove)
        document.addEventListener('mouseup', onUp)
    }
</script>

<template>
    <q-layout view="lHh LpR lff">
        <q-header elevated>
            <bz-main-toolbar />
        </q-header>

        <q-drawer
            v-model="isMenuOpen"
            side="left"
            behavior="desktop"
            elevated
            :width="menuWidth"
        >
            <bz-nav-menu />
            <div
                class="resize-handle resize-handle--right"
                @mousedown.prevent="startResize('left')"
            />
        </q-drawer>

        <q-drawer
            v-model="isLogOpen"
            side="right"
            behavior="desktop"
            bordered
            :width="logWidth"
        >
            <div
                class="resize-handle resize-handle--left"
                @mousedown.prevent="startResize('right')"
            />
            <div id="guidance" />
            <bz-section
                v-if="downloadsStore.hasDownloads"
                label="Downloads"
                header-class="bg-accent text-white"
                :model-value="true"
            >
                <downloads-drawer />
            </bz-section>
            <bz-section
                label="Contexts"
                header-class="bg-accent text-white"
                :model-value="true"
            >
                <bz-contexts />
            </bz-section>
            <bz-section
                label="Log"
                header-class="bg-accent text-white"
                :model-value="true"
            >
                <bz-log />
            </bz-section>
        </q-drawer>

        <q-page-container>
            <bz-flash-messages />
            <slot />
        </q-page-container>

        <flows-trigger-profile-dialog />
    </q-layout>
</template>

<style scoped>
    .resize-handle {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 4px;
        cursor: col-resize;
        z-index: 1;
    }

    .resize-handle--right {
        right: 0;
    }

    .resize-handle--left {
        left: 0;
    }

    .resize-handle:hover {
        background: rgba(128, 128, 128, 0.3);
    }
</style>
